import { useCallback, useEffect, useState } from 'react';
import { Bitcoin, Coins, RefreshCw, FilePlus2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import PageShell from '../components/PageShell';
import StatCard from '../components/StatCard';
import DataTable, { type Column } from '../components/DataTable';
import Modal from '../components/Modal';
import StatusPill from '../components/StatusPill';
import EmptyState from '../components/EmptyState';
import { ChartCard, ChartSkeleton, BarChart, CHART_COLORS } from '../components/charts';
import {
  controlClass,
  primaryButtonClass,
  secondaryButtonClass,
} from '../components/FilterBar';
import { formatDateTime } from '../lib/format';
import {
  fetchWalletBalances,
  fetchWalletActivity,
  stageCryptoJournalEntry,
  fetchPaymentoStatus,
  fetchPaymentoPayments,
  fetchPaymentoAddresses,
  fetchPaymentsChart,
  type PaymentoStatusResponse,
} from '../lib/api';

type Row = Record<string, unknown>;

function str(row: Row, ...keys: string[]): string | null {
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined && value !== null && value !== '' && typeof value !== 'object') {
      return String(value);
    }
  }
  return null;
}

const activityColumns: Column<Row>[] = [
  { key: 'date', header: 'Date', render: (r) => formatDateTime(str(r, 'created_time', 'timestamp', 'date')) },
  { key: 'wallet_type', header: 'Wallet', render: (r) => str(r, 'wallet_type') ?? '—' },
  { key: 'id', header: 'Reference', render: (r) => str(r, 'id', 'txid', 'hash') ?? '—' },
  {
    key: 'amount',
    header: 'Amount',
    align: 'right',
    render: (r) => `${str(r, 'amount', 'value') ?? '—'} ${str(r, 'asset', 'currency') ?? ''}`.trim(),
  },
  { key: 'status', header: 'Status', render: (r) => <StatusPill status={str(r, 'status') ?? 'posted'} /> },
];

const paymentoColumns: Column<Row>[] = [
  { key: 'order', header: 'Order', render: (r) => str(r, 'OrderId', 'order_id', 'orderId') ?? '—' },
  { key: 'payment', header: 'Payment ID', render: (r) => str(r, 'PaymentId', 'payment_id', 'id') ?? '—' },
  {
    key: 'status',
    header: 'Status',
    render: (r) => <StatusPill status={str(r, 'OrderStatus', 'status', 'event') ?? 'received'} />,
  },
  { key: 'token', header: 'Token', render: (r) => str(r, 'Token', 'token') ?? '—' },
];

function Crypto() {
  const [balances, setBalances] = useState<Row[]>([]);
  const [activity, setActivity] = useState<Row[]>([]);
  const [paymentoPayments, setPaymentoPayments] = useState<Row[]>([]);
  const [paymentoStatus, setPaymentoStatus] = useState<PaymentoStatusResponse | null>(null);
  const [paymentoAddresses, setPaymentoAddresses] = useState<Record<string, string>>({});
  const [balancesLoading, setBalancesLoading] = useState(true);
  const [activityLoading, setActivityLoading] = useState(true);
  const [paymentoLoading, setPaymentoLoading] = useState(true);
  const [paymentsChartLoading, setPaymentsChartLoading] = useState(true);
  const [paymentVolume, setPaymentVolume] = useState<Array<{ method: string; count: number; volume: number }>>([]);
  const [stageOpen, setStageOpen] = useState(false);

  const load = useCallback(async () => {
    setBalancesLoading(true);
    setActivityLoading(true);
    setPaymentoLoading(true);
    setPaymentsChartLoading(true);
    const [bal, act, payStatus, payList, payAddr, payChart] = await Promise.all([
      fetchWalletBalances('all'),
      fetchWalletActivity('all', 50, 0),
      fetchPaymentoStatus(),
      fetchPaymentoPayments(50, 0),
      fetchPaymentoAddresses(),
      fetchPaymentsChart(),
    ]);
    setBalances(bal.ok && bal.data ? bal.data.balances ?? [] : []);
    setActivity(act.ok && act.data ? act.data.data ?? [] : []);
    setPaymentoStatus(payStatus.ok && payStatus.data ? payStatus.data : null);
    setPaymentoPayments(payList.ok && payList.data ? payList.data.data ?? [] : []);
    setPaymentoAddresses(payAddr.ok && payAddr.data ? payAddr.data.addresses ?? {} : {});
    setPaymentVolume(payChart.ok && payChart.data ? payChart.data.data ?? [] : []);
    setBalancesLoading(false);
    setActivityLoading(false);
    setPaymentoLoading(false);
    setPaymentsChartLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const paymentoAddressCount = Object.keys(paymentoAddresses).length;

  return (
    <PageShell
      title="Crypto"
      subtitle="Paymento gateway, Monero wallet balances, and staged journal entries. BTCPay is disabled."
    >
      <ChartCard title="Payment volume by method" subtitle="Paymento · Chain2Pay · Stripe">
        {paymentsChartLoading ? (
          <ChartSkeleton />
        ) : paymentVolume.length === 0 ? (
          <EmptyState
            icon={<Coins size={28} />}
            title="No payment volume data"
            message="Configure Paymento, Chain2Pay, or Stripe to see volume by method."
          />
        ) : (
          <BarChart
            data={paymentVolume}
            xKey="method"
            series={[{ key: 'volume', label: 'Volume', color: CHART_COLORS[0] }]}
            valueFormatter={(v) => (v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${v.toFixed(0)}`)}
          />
        )}
      </ChartCard>

      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-white">Wallet balances</h3>
        <div className="flex gap-2">
          <button type="button" onClick={load} className={secondaryButtonClass}>
            <RefreshCw size={16} /> Refresh
          </button>
          <button type="button" onClick={() => setStageOpen(true)} className={primaryButtonClass}>
            <FilePlus2 size={16} /> Stage journal entry
          </button>
        </div>
      </div>

      {balancesLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-20 animate-pulse rounded-xl border border-zt-border bg-zt-surface" />
          ))}
        </div>
      ) : balances.length === 0 ? (
        <EmptyState
          icon={<Bitcoin size={32} />}
          title="No wallet balances"
          message="Configure Monero wallet RPC to see on-chain balances here."
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {balances.map((b, i) => (
            <StatCard
              key={str(b, 'asset', 'currency', 'wallet_type') ?? i}
              label={`${str(b, 'asset', 'currency') ?? 'Balance'}${str(b, 'wallet_type') ? ` · ${str(b, 'wallet_type')}` : ''}`}
              value={str(b, 'balance', 'amount', 'confirmed') ?? '—'}
              tone="info"
              icon={<Bitcoin size={18} />}
            />
          ))}
        </div>
      )}

      <div className="rounded-xl border border-zt-border bg-zt-surface p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Coins size={18} className="text-zt-accent" />
            <h3 className="font-semibold text-white">Paymento</h3>
            {paymentoStatus && (
              <StatusPill status={paymentoStatus.connected ? 'ok' : paymentoStatus.status} />
            )}
          </div>
          <span className="text-sm text-slate-400">
            {paymentoAddressCount} receive address{paymentoAddressCount === 1 ? '' : 'es'} configured
          </span>
        </div>

        {paymentoLoading ? (
          <div className="h-24 animate-pulse rounded-lg bg-zt-bg" />
        ) : !paymentoStatus?.configured ? (
          <EmptyState
            icon={<Coins size={28} />}
            title="Paymento not configured"
            message="Set PAYMENTO_API_KEY and receive addresses in .env, then redeploy dashboard-api."
          />
        ) : (
          <>
            {paymentoAddressCount > 0 && (
              <div className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {Object.entries(paymentoAddresses).map(([coin, addr]) => (
                  <div key={coin} className="rounded-lg border border-zt-border bg-zt-bg px-3 py-2 text-sm">
                    <div className="font-medium uppercase text-slate-300">{coin}</div>
                    <div className="truncate font-mono text-xs text-slate-400">{addr}</div>
                  </div>
                ))}
              </div>
            )}
            <DataTable
              columns={paymentoColumns}
              rows={paymentoPayments}
              rowKey={(r, i) => str(r, 'PaymentId', 'payment_id', 'id') ?? String(i)}
              loading={paymentoLoading}
              emptyTitle="No Paymento payments yet"
              emptyMessage="Payments appear here after IPN/webhook callbacks are received."
              emptyIcon={<Coins size={28} />}
            />
          </>
        )}
      </div>

      <div>
        <h3 className="mb-3 font-semibold text-white">Recent activity</h3>
        <DataTable
          columns={activityColumns}
          rows={activity}
          rowKey={(r, i) => str(r, 'id', 'txid', 'hash') ?? String(i)}
          loading={activityLoading}
          emptyTitle="No wallet activity"
          emptyMessage="Wallet transactions will appear here once configured."
          emptyIcon={<Bitcoin size={32} />}
        />
      </div>

      <StageJournalModal open={stageOpen} onClose={() => setStageOpen(false)} />
    </PageShell>
  );
}

function StageJournalModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [asset, setAsset] = useState('BTC');
  const [amount, setAmount] = useState('');
  const [usdValue, setUsdValue] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!asset.trim() || !amount.trim()) {
      toast.error('Asset and amount are required');
      return;
    }
    setSubmitting(true);
    const result = await stageCryptoJournalEntry({
      asset: asset.trim(),
      amount: amount.trim(),
      usd_value: usdValue.trim() || undefined,
      description: description.trim() || undefined,
    });
    setSubmitting(false);
    if (result.ok && result.data) {
      toast.success('Journal entry staged for approval');
      setAmount('');
      setUsdValue('');
      setDescription('');
      onClose();
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Stage crypto journal entry"
      description="This does not post to ERPNext. It creates an approval request that must be reviewed."
      footer={
        <>
          <Link to="/approvals" className={`${secondaryButtonClass} hidden sm:flex`}>
            View approvals
          </Link>
          <button type="button" onClick={onClose} className={secondaryButtonClass}>
            Cancel
          </button>
          <button type="button" onClick={handleSubmit} disabled={submitting} className={primaryButtonClass}>
            {submitting ? 'Staging…' : 'Stage for approval'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-200">Asset</label>
            <input
              type="text"
              value={asset}
              onChange={(e) => setAsset(e.target.value.toUpperCase())}
              className={`w-full ${controlClass}`}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-200">Amount</label>
            <input
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.05"
              className={`w-full ${controlClass}`}
            />
          </div>
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-200">USD value (optional)</label>
          <input
            type="text"
            inputMode="decimal"
            value={usdValue}
            onChange={(e) => setUsdValue(e.target.value)}
            placeholder="3200.00"
            className={`w-full ${controlClass}`}
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-slate-200">Description (optional)</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className={`w-full ${controlClass}`}
          />
        </div>
      </div>
    </Modal>
  );
}

export default Crypto;
